/**
 * ContactListContactsPage.tsx
 *
 * Default page for static lists — shows the contacts table.
 * For dynamic lists, redirects to the details/filters page.
 * Replicates legacy's email-contacts.php.
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Trash2, RotateCcw, Eye, EyeOff, Mail, Phone, Settings, Activity,
} from 'lucide-react';
import { DataTable } from '@/components/ui/DataTable';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { usePageContext } from '@/hooks/usePageContext';
import { cn, formatPhone } from '@/lib/utils';
import { PatientSearchInput } from '@/components/medical/PatientSearchInput';
import * as marketingSvc from '@/services/marketing.service';
import type { ListMember } from '@/services/marketing.service';

export default function ContactListContactsPage() {
  const { listId: listIdParam } = useParams<{ listId: string }>();
  const listId = Number(listIdParam);
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const updateAIContext = usePageContext('List Contacts');

  useEffect(() => {
    updateAIContext({ dataSummary: `Viewing contacts for list #${listId}` });
  }, [updateAIContext, listId]);

  const { data: list, isLoading: listLoading } = useQuery({
    queryKey: ['marketing', 'list', listId],
    queryFn: () => marketingSvc.getAudienceList(listId),
    enabled: !Number.isNaN(listId),
  });

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showRemoved, setShowRemoved] = useState(false);
  const [automationsOpen, setAutomationsOpen] = useState(false);

  const membersQuery = useQuery({
    queryKey: ['marketing', 'list-members', listId, search, page, showRemoved],
    queryFn: () => marketingSvc.getListMembers(listId, { page, perPage: 25, search, showRemoved }),
    enabled: !!list && list.listType === 'static',
  });

  const removeMemberMut = useMutation({
    mutationFn: (memberId: number) => marketingSvc.removeListMember(listId, memberId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'list-members', listId] });
      qc.invalidateQueries({ queryKey: ['marketing', 'list', listId] });
      toast.success('Contact removed');
    },
  });

  const restoreMemberMut = useMutation({
    mutationFn: (memberId: number) => marketingSvc.restoreListMember(listId, memberId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'list-members', listId] });
      qc.invalidateQueries({ queryKey: ['marketing', 'list', listId] });
      toast.success('Contact restored');
    },
  });

  const addMemberMut = useMutation({
    mutationFn: (patientId: number) => marketingSvc.addListMember(listId, patientId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'list-members', listId] });
      qc.invalidateQueries({ queryKey: ['marketing', 'list', listId] });
      toast.success('Patient added to list');
    },
    onError: () => toast.error('Failed to add patient'),
  });

  const toggleActiveMut = useMutation({
    mutationFn: (newActive: boolean) => marketingSvc.updateAudienceList(listId, { active: newActive }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'list', listId] });
      qc.invalidateQueries({ queryKey: ['marketing', 'lists'] });
    },
    onError: () => toast.error('Failed to update status'),
  });

  if (listLoading || !list) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" label="Loading list..." />
      </div>
    );
  }

  // Dynamic lists redirect to their details/filters page
  if (list.listType === 'dynamic') {
    return <Navigate to={`/marketing/contact-lists/${listId}/details`} replace />;
  }

  const hasLinkedAutomations = (list.linkedAutomations?.length ?? 0) > 0;
  const canDeactivate = !hasLinkedAutomations;

  const handleToggleActive = () => {
    if (list.active && !canDeactivate) {
      toast.error('This list cannot be deactivated because it is used by an automation.');
      return;
    }
    toggleActiveMut.mutate(!list.active);
  };

  return (
    <div className="p-5 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-slate-200 mb-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/marketing/contact-lists')} leftIcon={<ArrowLeft className="h-4 w-4" />}>
            Back
          </Button>
          <h2 className="text-lg font-semibold text-slate-900">List Contacts: {list.name}</h2>
          <Badge className="bg-[#e3f2fd] text-[#1565c0] ring-[#1565c0]/20">Static</Badge>
        </div>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<Settings className="h-4 w-4" />}
          onClick={() => navigate(`/marketing/contact-lists/${listId}/details`)}
        >
          List Details
        </Button>
      </div>

      {/* Stats bar + Active toggle + Add patient + Show Removed + Search */}
      <div className="flex items-center gap-4 pb-4 border-b border-slate-200 mb-4 flex-shrink-0 flex-wrap">
        <span className="text-[28px] font-semibold text-[#00aff0]">{(list.patientCount ?? 0).toLocaleString()}</span>
        <span className="text-sm text-slate-500">contacts</span>
        <span className="inline-flex items-center gap-1 text-[13px] font-medium text-[#888] bg-[#f5f5f5] rounded-full px-2.5 py-1">
          <Mail className="h-3.5 w-3.5 text-[#aaa]" fill="currentColor" stroke="white" strokeWidth={1.5} />
          {(list.uniqueEmailCount ?? 0).toLocaleString()}
        </span>
        <span className="inline-flex items-center gap-1 text-[13px] font-medium text-[#888] bg-[#f5f5f5] rounded-full px-2.5 py-1">
          <Phone className="h-3.5 w-3.5 text-[#aaa]" fill="currentColor" stroke="white" strokeWidth={1.5} />
          {(list.uniquePhoneCount ?? 0).toLocaleString()}
        </span>

        {/* Active toggle */}
        <div className="border-l border-[#e5e5e5] pl-4 ml-2 flex items-center gap-2.5">
          <label className="relative inline-block w-[50px] h-[26px] cursor-pointer">
            <input
              type="checkbox"
              checked={list.active}
              onChange={handleToggleActive}
              className="sr-only peer"
            />
            <span className="absolute inset-0 rounded-full bg-[#ccc] transition-colors duration-300 peer-checked:bg-[#28a745]" />
            <span className="absolute left-[3px] bottom-[3px] h-5 w-5 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.2)] transition-transform duration-300 peer-checked:translate-x-6" />
          </label>
          <span className={cn('text-[13px] font-semibold min-w-[55px]', list.active ? 'text-[#28a745]' : 'text-[#999]')}>
            {list.active ? 'Active' : 'Inactive'}
          </span>
        </div>

        {/* Automations indicator */}
        {hasLinkedAutomations && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setAutomationsOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors"
            >
              <Activity className="h-3.5 w-3.5 text-slate-400" />
              {list.linkedAutomations.length}
            </button>
            {automationsOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setAutomationsOpen(false)} />
                <div className="absolute top-full left-0 mt-1 z-20 bg-white border border-slate-200 rounded-lg shadow-lg p-3 min-w-[220px]">
                  <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Used by Automations</p>
                  <div className="space-y-1.5">
                    {list.linkedAutomations.map((auto) => (
                      <div
                        key={`${auto.channel}-${auto.id}`}
                        className="flex items-center justify-between gap-2 px-2 py-1.5 rounded text-sm text-slate-700 hover:bg-slate-50"
                      >
                        <span className="truncate">{auto.name}</span>
                        <span className={cn(
                          'text-[10px] px-2 py-0.5 rounded-full font-semibold shrink-0',
                          auto.status === 'active' && 'bg-green-100 text-green-700',
                          auto.status === 'paused' && 'bg-yellow-100 text-yellow-700',
                          auto.status === 'draft' && 'bg-slate-100 text-slate-500',
                          !['active', 'paused', 'draft'].includes(auto.status) && 'bg-slate-100 text-slate-500',
                        )}>
                          {auto.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Add patient */}
        <div className="border-l border-slate-200 pl-4 ml-2">
          <div className="w-64">
            <PatientSearchInput
              placeholder="Add patient..."
              onSelect={(patient) => {
                if (patient) addMemberMut.mutate(patient.patientId);
              }}
            />
          </div>
        </div>

        {/* Show Removed toggle */}
        <button
          type="button"
          onClick={() => { setShowRemoved((v) => !v); setPage(1); }}
          className={cn(
            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors',
            showRemoved
              ? 'bg-blue-50 border-blue-300 text-blue-600'
              : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50',
          )}
        >
          {showRemoved ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {showRemoved ? 'Hide Removed' : 'Show Removed'}
        </button>

        {/* Search */}
        <div className="ml-auto">
          <Input
            placeholder="Search contacts..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-52"
          />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <DataTable<ListMember>
          keyField="id"
          columns={[
            {
              key: 'email',
              header: 'Email',
              render: (r) => (
                <div className={cn(!r.active && 'text-slate-400')}>
                  <span>{r.email || '--'}</span>
                  {r.patientId ? (
                    <span className={cn('block text-[11px]', r.active ? 'text-slate-400' : 'text-slate-300')}>
                      Patient ID: {r.patientId}
                    </span>
                  ) : null}
                </div>
              ),
            },
            {
              key: 'phone',
              header: 'Phone',
              render: (r) => (
                <span className={cn(!r.active && 'text-slate-400')}>
                  {r.phone ? formatPhone(r.phone, r.countryCode || '1') : '--'}
                </span>
              ),
            },
            {
              key: 'firstName',
              header: 'First Name',
              render: (r) => (
                <span className={cn(!r.active && 'text-slate-400')}>{r.firstName || '--'}</span>
              ),
            },
            {
              key: 'lastName',
              header: 'Last Name',
              render: (r) => (
                <span className={cn(!r.active && 'text-slate-400')}>{r.lastName || '--'}</span>
              ),
            },
            {
              key: 'active',
              header: 'Status',
              render: (r) => (
                <Badge variant={r.active ? 'success' : 'neutral'} className={cn(!r.active && 'bg-slate-100 text-slate-400')}>
                  {r.active ? 'Active' : 'Removed'}
                </Badge>
              ),
            },
            {
              key: 'addedAt',
              header: 'Added',
              render: (r) => (
                <span className={cn(!r.active && 'text-slate-400')}>
                  {r.addedAt ? new Date(r.addedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '--'}
                </span>
              ),
            },
            {
              key: 'actions',
              header: '',
              render: (r) => r.active ? (
                <button
                  type="button"
                  title="Remove from list"
                  onClick={(e) => { e.stopPropagation(); removeMemberMut.mutate(r.id); }}
                  className="p-1.5 text-slate-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              ) : (
                <button
                  type="button"
                  title="Restore to list"
                  onClick={(e) => { e.stopPropagation(); restoreMemberMut.mutate(r.id); }}
                  className="p-1.5 text-slate-400 hover:text-green-600 transition-colors"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
              ),
            },
          ]}
          data={membersQuery.data?.data ?? []}
          loading={membersQuery.isLoading}
          page={membersQuery.data?.meta?.page ?? 1}
          pageSize={25}
          total={membersQuery.data?.meta?.total ?? 0}
          onPageChange={setPage}
          emptyTitle="No contacts"
          emptyDescription="Add contacts to this list to get started."
        />
      </div>
    </div>
  );
}
